import React from 'react';
import { useGetProductReviewsQuery } from '../../../redux/apis/endpoints/reviewApi';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorMessage from '../../../components/common/ErrorMessage';
import { StarIcon } from '@heroicons/react/24/solid';
import { UserCircleIcon } from '@heroicons/react/24/outline';

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric'
  });
};

const ProductReviewsDisplay = ({ productId }) => {
  const { 
    data: reviewsData,
    isLoading,
    isError,
    error
  } = useGetProductReviewsQuery(
    { productId, strapiV3: true, sort: 'createdAt:desc', pageSize: 100 }, // Fetch more reviews for display
    { skip: !productId }
  );

  if (isLoading) return <div className="py-8"><LoadingSpinner /></div>;
  if (isError) return <ErrorMessage message={error?.data?.message || 'Could not load reviews.'} />;

  const reviews = reviewsData || []; // Strapi v3 might return array directly, v4 has it in data.data

  return (
    <div className="mt-12 pt-8 border-t border-gray-200">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Customer Reviews</h2>
      {reviews.length === 0 ? (
        <p className="text-gray-600">No reviews yet for this product.</p>
      ) : (
        <div className="space-y-8">
          {reviews.map((review) => (
            <div key={review.id} className="border-b border-gray-100 pb-6 last:border-b-0 last:pb-0">
              <div className="flex items-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <StarIcon 
                    key={i} 
                    className={`h-5 w-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                  />
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-3">
                {review.comment}
              </p>
              <div className="flex items-center text-sm text-gray-500 mb-3">
                <UserCircleIcon className="h-5 w-5 mr-1.5 text-gray-400" />
                <span>{review.customers?.[0]?.username || review.user?.username || 'Anonymous User'}</span>
                <span className="mx-2">|</span>
                <span>{formatDate(review.createdAt || review.created_at)}</span>
              </div>
              {review.pictures && review.pictures.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {review.pictures.map(pic => (
                    <img 
                      key={pic.id} 
                      src={pic.url} 
                      alt={`Review image ${pic.id}`}
                      className="w-20 h-20 rounded-md object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                      // onClick={() => handleImageClick(pic.url)} // Optional: Add a larger preview later
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviewsDisplay; 